/**
 * Windows Media (ASF) — the one accepted upload format the browser can do
 * nothing with. It cannot decode it (so no language sample: audioSample.ts),
 * cannot play it, and cannot read its duration off a media element — which
 * is what the chunking decision ran on, so a long WMA was never cut into
 * parts and went as one request, straight into the timeout the chunking
 * exists to prevent.
 *
 * The duration, at least, is written in the file. The ASF Header Object
 * carries a File Properties Object with the play duration and the preroll,
 * and play − preroll is exactly what ffprobe reports: measured to 0.0 ms on
 * every file tried — 19 s to 46 min, WMAv1 and WMAv2, headers from 452 B to
 * 10 KB. Reading it costs two small slices of the file: no ffmpeg download,
 * no media element, and no visible tab, which Chrome's media element needs
 * before it will load metadata at all.
 */

const WMA_EXT = /\.wma$/i
const WMA_TYPES = new Set(['audio/x-ms-wma', 'audio/wma'])

export function isWma(file: Pick<File, 'name' | 'type'>): boolean {
  return WMA_EXT.test(file.name) || WMA_TYPES.has(file.type)
}

/** A GUID as it sits on disk: the first three fields little-endian. */
export function guidBytes(guid: string): Uint8Array {
  const h = guid.replace(/-/g, '')
  const le = (s: string) => (s.match(/../g) as string[]).reverse()
  const hex = [...le(h.slice(0, 8)), ...le(h.slice(8, 12)), ...le(h.slice(12, 16)), ...(h.slice(16).match(/../g) as string[])]
  return Uint8Array.from(hex, (x) => parseInt(x, 16))
}

export const ASF_HEADER_OBJECT = guidBytes('75B22630-668E-11CF-A6D9-00AA0062CE6C')
export const ASF_FILE_PROPERTIES = guidBytes('8CABDCA1-A947-11CF-8EE4-00C00C205365')

function sameGuid(buf: Uint8Array, at: number, guid: Uint8Array): boolean {
  if (at + 16 > buf.length) return false
  for (let i = 0; i < 16; i++) if (buf[at + i] !== guid[i]) return false
  return true
}

/**
 * play − preroll from an ASF header, in seconds; null when these bytes are
 * not one or do not reach its File Properties Object.
 *
 * The children are walked by their declared sizes rather than assumed to put
 * File Properties first — they usually do, and "usually" is the kind of
 * assumption that holds on every test file and breaks on the first real one.
 */
export function asfDurationSeconds(head: Uint8Array): number | null {
  if (head.length < 30 || !sameGuid(head, 0, ASF_HEADER_OBJECT)) return null
  const v = new DataView(head.buffer, head.byteOffset, head.byteLength)
  const children = v.getUint32(24, true)
  let o = 30
  for (let i = 0; i < children && o + 24 <= head.length; i++) {
    const size = Number(v.getBigUint64(o + 16, true))
    if (sameGuid(head, o, ASF_FILE_PROPERTIES)) {
      // After the 24-byte object header: file id 16, file size 8, creation
      // date 8, packet count 8, play duration 8, send duration 8, preroll 8
      const body = o + 24
      if (body + 64 > head.length) return null
      const play = Number(v.getBigUint64(body + 40, true)) / 1e7 // 100-ns units
      const preroll = Number(v.getBigUint64(body + 56, true)) / 1e3 // milliseconds
      const seconds = play - preroll
      return Number.isFinite(seconds) && seconds > 0 ? seconds : null
    }
    // A size under a bare object header would walk in place for ever
    if (size < 24) return null
    o += size
  }
  return null
}

/** The Header Object states its own size in its first 30 bytes. Tags and
 * cover art can make it large, so it is read to that size — up to a ceiling,
 * past which the file is treated as having no readable duration. */
const MAX_HEADER_BYTES = 4 * 1024 * 1024

export async function wmaDurationSeconds(file: Blob): Promise<number | null> {
  try {
    const first = new Uint8Array(await file.slice(0, 30).arrayBuffer())
    if (!sameGuid(first, 0, ASF_HEADER_OBJECT)) return null
    const size = Number(new DataView(first.buffer).getBigUint64(16, true))
    const head = new Uint8Array(await file.slice(0, Math.min(size, MAX_HEADER_BYTES)).arrayBuffer())
    return asfDurationSeconds(head)
  } catch {
    return null
  }
}
